<script setup>
// Pemberitahuan versi baru untuk staf. Sumber kebenarannya /version.json di
// server (bukan status service worker): popup tampil bila versi server lebih
// baru dari versi bundle yang sedang berjalan. Service worker baru langsung
// aktif (skipWaiting), jadi "Muat ulang & perbarui" cukup memastikan worker
// terbaru sudah memegang halaman lalu reload.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'

// Cek tiap 2 menit + setiap tab kembali aktif, supaya rilis cepat sampai.
const UPDATE_CHECK_INTERVAL = 2 * 60 * 1000
const MIN_CHECK_GAP = 30 * 1000
const SNOOZE_DURATION = 30 * 60 * 1000
const WORKER_WAIT = 10 * 1000
const RELOAD_GUARD_KEY = 'pitscore:pwa-update-attempt'
const currentVersion = typeof __APP_VERSION_DISPLAY__ !== 'undefined' ? __APP_VERSION_DISPLAY__ : '-'

const snoozed = ref(false)
const reloading = ref(false)
const blockedAttempt = ref(false)
const errorMessage = ref('')
const latestVersion = ref('')
const releaseSummary = ref('Pembaruan pitscore sudah siap dipakai.')
const highlights = ref([])
let registration = null
let checkTimer = null
let snoozeTimer = null
let lastCheck = 0

const targetVersion = computed(() => latestVersion.value || 'versi terbaru')

const versionParts = (value) => String(value || '').split('.').map((part) => Number.parseInt(part, 10) || 0)
const isNewerVersion = (candidate, installed) => {
  const a = versionParts(candidate)
  const b = versionParts(installed)
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) > (b[i] || 0)
  }
  return false
}
const hasNewerVersion = computed(() => isNewerVersion(latestVersion.value, currentVersion))

// Percobaan sebelumnya berhasil bila versi terpasang sudah mencapai tujuannya.
const readGuard = () => { try { return sessionStorage.getItem(RELOAD_GUARD_KEY) || '' } catch { return '' } }
const writeGuard = (value) => { try { value ? sessionStorage.setItem(RELOAD_GUARD_KEY, value) : sessionStorage.removeItem(RELOAD_GUARD_KEY) } catch { /* abaikan */ } }
if (readGuard() && !isNewerVersion(readGuard(), currentVersion)) writeGuard('')

const loadReleaseInfo = async () => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return
    const info = await response.json()
    latestVersion.value = info.version || ''
    releaseSummary.value = info.summary || releaseSummary.value
    highlights.value = Array.isArray(info.highlights) ? info.highlights.slice(0, 3) : []
    // Sudah reload untuk versi tujuan yang sama tapi masih versi lama: jangan ulangi.
    blockedAttempt.value = readGuard() === latestVersion.value
    if (blockedAttempt.value) errorMessage.value = 'Versi baru belum termuat. Tutup semua tab pitscore (atau aplikasinya) lalu buka kembali.'
  } catch (error) {
    console.warn('Informasi versi terbaru tidak dapat dimuat', error)
  }
}

const checkForUpdate = (force = false) => {
  if (!force && (document.visibilityState !== 'visible' || !navigator.onLine)) return
  if (!force && Date.now() - lastCheck < MIN_CHECK_GAP) return
  lastCheck = Date.now()
  loadReleaseInfo()
  registration?.update().catch(() => {})
}
const onVisible = () => checkForUpdate()

// Alamat bernomor versi: Cloudflare menyimpan /sw.js sampai 4 jam, sedangkan
// /sw.js?v=x.y.z selalu baru bagi cache sehingga versi rilis langsung terpasang.
// updateViaCache 'none': browser tidak memakai cache HTTP untuk file worker.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .register(`/sw.js?v=${encodeURIComponent(currentVersion)}`, { scope: '/', updateViaCache: 'none' })
    .then((reg) => { registration = reg })
    .catch((error) => console.error('Service worker gagal didaftarkan', error))
}

// Tunggu worker yang sedang dipasang selesai & mengambil alih (maks. WORKER_WAIT).
const waitForWorker = () => new Promise((done) => {
  if (!registration || !('serviceWorker' in navigator)) return done()
  const pending = registration.installing || registration.waiting
  if (!pending) return done()
  const timer = setTimeout(done, WORKER_WAIT)
  navigator.serviceWorker.addEventListener('controllerchange', () => { clearTimeout(timer); done() }, { once: true })
  pending.postMessage({ type: 'SKIP_WAITING' })
})

const applyUpdate = async () => {
  if (reloading.value || blockedAttempt.value) return
  reloading.value = true
  errorMessage.value = ''
  writeGuard(latestVersion.value)
  try {
    await registration?.update()
  } catch { /* tetap coba reload */ }
  await waitForWorker()
  window.location.reload()
}

const snooze = () => {
  snoozed.value = true
  clearTimeout(snoozeTimer)
  snoozeTimer = setTimeout(() => { snoozed.value = false }, SNOOZE_DURATION)
}

onMounted(() => {
  checkForUpdate(true)
  checkTimer = setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL)
  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('focus', onVisible)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisible)
  window.removeEventListener('focus', onVisible)
  clearInterval(checkTimer)
  clearTimeout(snoozeTimer)
})
</script>

<template>
  <Transition name="update-banner">
    <aside
      v-if="hasNewerVersion && !snoozed"
      class="fixed inset-x-0 bottom-0 z-40 p-3 sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-full sm:max-w-md sm:p-0"
      role="status"
      aria-live="polite"
      aria-label="Pembaruan aplikasi tersedia"
    >
      <div class="overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-slate-900/10">
        <div class="flex items-start gap-3 p-4">
          <span class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#B70000]/10 text-[#B70000]">
            <ArrowPathIcon class="h-5 w-5" :class="reloading ? 'animate-spin' : ''" />
          </span>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-sm font-bold text-slate-950">Versi baru siap digunakan</p>
              <span v-if="latestVersion" class="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-800">v{{ latestVersion }}</span>
            </div>
            <p class="mt-1 text-xs leading-5 text-slate-600">{{ releaseSummary }}</p>
            <ul v-if="highlights.length" class="mt-2 space-y-1 text-xs text-slate-700">
              <li v-for="item in highlights" :key="item" class="flex gap-1.5">
                <CheckCircleIcon class="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <span>{{ item }}</span>
              </li>
            </ul>
            <p class="mt-2 text-[11px] font-medium text-amber-800">Simpan pekerjaan yang sedang diisi sebelum memperbarui.</p>
            <p v-if="errorMessage" class="mt-2 flex gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold leading-5 text-amber-950" role="alert">
              <ExclamationTriangleIcon class="mt-0.5 h-4 w-4 shrink-0" />
              <span>{{ errorMessage }}</span>
            </p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button type="button" class="min-h-9 rounded-xl bg-[#B70000] px-3.5 text-xs font-bold text-white transition-colors hover:bg-[#930000] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700/30 disabled:cursor-not-allowed disabled:bg-slate-300" :disabled="reloading || blockedAttempt" @click="applyUpdate">
                {{ reloading ? 'Menerapkan…' : blockedAttempt ? 'Sudah dicoba' : 'Muat ulang & perbarui' }}
              </button>
              <button type="button" class="min-h-9 rounded-xl px-3 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400" @click="snooze">Nanti saja</button>
            </div>
          </div>
          <button type="button" class="shrink-0 rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400" aria-label="Tutup pemberitahuan" @click="snooze">
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>
        <p class="bg-slate-50 px-4 py-2 text-[11px] text-slate-600">Terpasang v{{ currentVersion }} <span aria-hidden="true">→</span> {{ latestVersion ? `tersedia v${targetVersion}` : 'versi terbaru tersedia' }}</p>
      </div>
    </aside>
  </Transition>
</template>

<style scoped>
.update-banner-enter-active,
.update-banner-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.update-banner-enter-from,
.update-banner-leave-to { opacity: 0; transform: translateY(12px); }
@media (prefers-reduced-motion: reduce) {
  .update-banner-enter-active,
  .update-banner-leave-active { transition: none; }
  .update-banner-enter-from,
  .update-banner-leave-to { transform: none; }
}
</style>
